import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DeviceCatalogEntry,
  OnboardingRequestItem,
  ProposedDeviceItem,
  ProposedVendorItem,
  RequestsInbox,
  VendorDirectoryEntry,
} from '@mvcp/contracts';
import { ConfirmModal } from './ConfirmModal';
// Story 8.5 (AD-25): the label AND the revision vocabulary come from the one web owner.
// This view used to declare `revision()` and the distinguishing predicate itself, which
// was a third copy of a rule the same story had just given an owner.
import { deviceLabel, deviceRevision, mergeDestroysRevision } from './device-label';
// Story 8.12: the candidate shortlist and the ONE web-local owner of the strength wording.
import { DuplicateCandidates } from './DuplicateCandidates';
import {
  curateProposedDevice,
  curateProposedVendor,
  getDevices,
  getRequestsInbox,
  getVendors,
  mergeProposedDevice,
  mergeProposedVendor,
  resolveOnboarding,
  verifyProposedDevice,
  verifyProposedVendor,
} from '../api';

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/** A minimal email shape check — used only to decide whether to prefill the onboard field. */
function looksLikeEmail(value: string | undefined): value is string {
  return typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

interface Loaded {
  inbox: RequestsInbox;
  /** Backbone (curated) Vendors — the merge-into targets for a proposed Vendor. */
  backboneVendors: VendorDirectoryEntry[];
  /** Backbone (curated) Devices — the merge-into targets for a proposed Device. */
  backboneDevices: DeviceCatalogEntry[];
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: Loaded }
  | { kind: 'error'; message: string };

/**
 * The ONE action failure on screen, and which action produced it (Story 8.10).
 *
 * A single value rather than a map keyed by row: `submitting` already serialises the
 * section's actions, so at most one failure can be current — and a map would leave stale
 * refusals sitting on rows the administrator has already moved past.
 *
 * `key` is `<action>:<rowId>` (`vendor-verify:…`, `vendor-merge:…`, `device-verify:…`,
 * `device-merge:…`, `onboard:…`). The action half exists so a caller names what it ran;
 * the row half is what decides WHERE the message renders.
 */
interface ActionError {
  key: string;
  message: string;
}

/** The row an action key belongs to: `device-verify:<id>` → `<id>`. Ids are UUIDs, no colons. */
function rowOf(key: string): string {
  return key.slice(key.indexOf(':') + 1);
}

/**
 * The client half of the blocked-device rule. The server states the same rule at
 * `admin-requests.repository.ts:274` (`'Verify the owning Vendor first'`) — these are two
 * DELIBERATE wordings of ONE rule, not drift: the client holds the inbox in hand and can
 * therefore name the blocking Vendor, which the repository (given only an id) cannot.
 * When no matching proposal is on the page the sentence degrades to the server's own
 * wording rather than rendering an empty name.
 */
function blockedReason(vendorName: string | null): string {
  return vendorName === null
    ? 'Verify the owning Vendor first.'
    : `Verify the owning Vendor ${vendorName} first — it is itself a proposal.`;
}

/**
 * One editable field on the "Edit before verifying" form (Story 8.12).
 *
 * `required` is the `model` / `name` distinction the server enforces: a required field may be
 * neither cleared nor blanked (400 either way), while an optional one is cleared by emptying
 * it completely and a blank in it is a 400 rather than a silent clear (AR-19).
 */
interface CurationField {
  /** The contract key, which is also the wire key and the audit `detail` key. */
  field: string;
  label: string;
  required: boolean;
  /** The value as STORED — `null` when the DTO omitted the key (genuinely absent). */
  stored: string | null;
}

/** The proposal being curated, its field descriptors and the draft for each. */
interface Editing {
  kind: 'vendor' | 'device';
  id: string;
  /** How the row read when it was opened — how the administrator identified it. */
  subject: string;
  fields: CurationField[];
  draft: Record<string, string>;
}

/**
 * The value a Save would WRITE for one field, and whether it is a change at all (Story 8.12).
 *
 * BOTH SIDES TRIMMED, and a CLEAR compared against the STORED BYTES — the `CuratedDevicesView`
 * lesson in both directions, and it took two bugs to learn:
 *   * comparing untrimmed meant opening a padded legacy row and pressing Save with nothing
 *     typed wrote and AUDITED a change the administrator never made;
 *   * comparing a clear against the TRIMMED stored value meant a whitespace-only stored value
 *     was unclearable — `'   '` trims to `''`, so emptying the field compared equal and the
 *     form answered "No changes to save." while the server would happily have cleared it.
 */
function intendedChange(f: CurationField, draft: string): { to: string | null; changed: boolean } {
  const trimmed = draft.trim();
  const to = draft === '' ? null : trimmed;
  const changed = to === null ? f.stored !== null : trimmed !== (f.stored ?? '').trim();
  return { to, changed };
}

/**
 * A row's own error surface (Story 8.10) — the codebase's inline-error idiom, copied from
 * `VerificationQueueView.tsx:183-190`.
 *
 * Rendered UNCONDITIONALLY (empty when there is nothing to say) so its `id` is stable and
 * the `aria-describedby` on the row's controls always resolves — same shape as
 * `ConfirmModal.tsx:75-83`. There is exactly one of these per row and no page-level
 * duplicate: two live regions carrying one sentence make a screen reader announce the
 * failure twice (AD-22 rule 7's one-spoken-source-per-region rule).
 */
function RowErrorSlot({ id, message }: { id: string; message: string | null }): React.JSX.Element {
  return (
    <div id={id} role="alert" aria-live="assertive">
      {message && (
        <span className="status-pill" data-tone="bad">
          <span className="status-pill__dot" aria-hidden="true" />
          {message}
        </span>
      )}
    </div>
  );
}

/**
 * Admin Console → Requests inbox (Story 6.4, FR-10/FR-12). The System-Administrator
 * surface that resolves the two dead-ends deferred from earlier epics:
 *   * Manager-proposed Vendors/Devices awaiting curation — each row offers **Verify**
 *     (promote under the same id) and **Merge into…** (dedup into an existing backbone
 *     record). A proposed Device whose owning Vendor is still proposed cannot be
 *     verified (the API 409s — "verify the Vendor first").
 *   * Info-requests (FR-12) targeting a Vendor with no portal account — each row offers
 *     **Onboard vendor** (an email, prefilled from the Vendor's compliance contact when
 *     it looks like an email), which mints the FR-3 magic-link invite and surfaces the
 *     dev activation link.
 * A single `submitting` guard prevents double-submits; success is announced via one shared
 * `role="status"` region, and a failure is announced by the ACTING ROW'S OWN `role="alert"`
 * slot (Story 8.10 — the page-level banner this view used to render sat under the `<h1>`
 * while the control that failed could be three sections below it, so every refusal read as
 * a dead button). The list refreshes after each action (OUTSIDE the action try/catch, so a
 * refresh failure never masks a successful action). System-Administrator-only (belt with
 * the API guard).
 */
export function RequestsInboxView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  // Per-row merge-target selections + onboard emails (keyed by proposal / flag id).
  const [mergeTargets, setMergeTargets] = useState<Record<string, string>>({});
  const [emails, setEmails] = useState<Record<string, string>>({});
  /**
   * A device merge held for explicit confirmation (Story 8.5): the chosen target carries
   * a different revision from the proposal, so the merge would DESTROY the loser's. The
   * request is not sent until the admin confirms; cancelling sends nothing.
   */
  const [pendingMerge, setPendingMerge] = useState<{
    loser: ProposedDeviceItem;
    winner: DeviceCatalogEntry;
  } | null>(null);
  /**
   * The confirmed merge's failure, shown INSIDE the dialog. There is deliberately no
   * separate busy flag: the dialog's `busy` is the section's shared `submitting`, so the
   * confirmed merge cannot double-submit past `act()`'s guard (Story 8.5).
   */
  const [mergeError, setMergeError] = useState<string | null>(null);
  /**
   * Whether the confirm dialog is still on screen when the confirmed merge's failure arrives.
   *
   * `ConfirmModal` dismisses on Esc and on a scrim click WITHOUT consulting `busy` (only its
   * two buttons are gated), so an administrator can close this dialog while the POST is in
   * flight. The failure was then routed into `mergeError` alone — state belonging to a dialog
   * that no longer exists — while the section's own alert was never touched, so a DESTRUCTIVE
   * action failed with no feedback on any surface and the row stayed in the inbox looking
   * untouched. Worse than the pre-8.5 behaviour, which at least surfaced it inline.
   *
   * A ref rather than state because the failure handler runs outside React's render cycle and
   * needs the value as of that moment, not as of the closure it was created in. The dialog
   * staying dismissible while busy is logged separately — this only guarantees that wherever
   * the dialog has gone, the failure still lands somewhere a human can see.
   */
  const mergeDialogOpen = useRef(false);
  /**
   * Each proposed Vendor row's Verify button, keyed by vendor id (Story 8.10). A blocked
   * Device row names the Vendor that blocks it and offers a control that moves focus HERE
   * — the Vendor is already on this page, so a route (or an anchor that scrolls) would be
   * machinery for a trip of a few hundred pixels.
   */
  const vendorVerifyRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  /**
   * The proposal open in the "Edit before verifying" form (Story 8.12), or null.
   *
   * Its four dialog behaviours are READ OUT OF `CuratedDevicesView` rather than re-derived —
   * `ConfirmModal` as a form shell with `aria-label`led inputs and `confirmLabel: 'Save'`; the
   * trimmed-both-sides no-op test; the separate POLITE advisory slot for the messages that are
   * not errors; and the two-step discard warning that re-arms on further typing. Each of those
   * exists because of a specific bug, all four documented there.
   */
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  /** A server failure from the Save, kept INSIDE the open dialog (ConfirmModal contract). */
  const [editError, setEditError] = useState<string | null>(null);
  /**
   * The dialog's POLITE slot — "No changes to save.", the blank-is-not-a-clear advice and the
   * unsaved-changes prompt. None of the three is an error, so none may land in
   * `ConfirmModal`'s `role="alert"` `aria-live="assertive"` region, which stays reserved for a
   * server refusal: an advisory appears while the administrator is still in the form and must
   * not interrupt.
   */
  const [editAdvisory, setEditAdvisory] = useState<string | null>(null);
  /** A dismissal that would discard typed input is WARNED once; further typing re-arms it. */
  const [discardWarned, setDiscardWarned] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [inbox, vendors, devices] = await Promise.all([
        getRequestsInbox(),
        getVendors(),
        getDevices(),
      ]);
      setState({
        kind: 'ready',
        data: {
          inbox,
          backboneVendors: vendors.filter((e) => !e.vendor.proposedByCompanyId),
          backboneDevices: devices.filter((e) => !e.device.proposedByCompanyId),
        },
      });
      // Seed onboard emails from each Vendor's compliance contact when it looks like one.
      setEmails((prev) => {
        const next = { ...prev };
        for (const f of inbox.onboardingFlags) {
          if (next[f.id] === undefined) {
            next[f.id] = looksLikeEmail(f.vendorComplianceContact) ? f.vendorComplianceContact : '';
          }
        }
        return next;
      });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** The message owed to one row, or null. Exactly one row can be non-null at a time. */
  function errorFor(rowKey: string): string | null {
    return actionError !== null && rowOf(actionError.key) === rowKey ? actionError.message : null;
  }

  /**
   * Put a message on a row WITHOUT sending anything (Story 8.10) — the client-side
   * short-circuit for a Verify that the inbox already knows the server would refuse.
   *
   * It clears NOTHING but the previous refusal (AA's call, review round 3). `act()` clears
   * the notice and the link on entry because a real action REPLACES the previous outcome;
   * a short-circuit produces no outcome to replace it with. The concrete cost of mirroring
   * `act()` here was that pressing a greyed-out button destroyed the one-time activation
   * link a just-resolved onboarding flag had put in the page notice — the only place it
   * ever renders, and not re-mintable, because the flag is now `resolved` and
   * `hasPendingInvitation` suppresses a second invite. A control that sends nothing must
   * not be able to destroy unrecoverable output.
   */
  function refuse(key: string, message: string): void {
    setActionError({ key, message });
  }

  /**
   * Run one curation action under the shared submit guard, then refresh on success.
   *
   * `key` is `<action>:<rowId>` (Story 8.10): a failure renders in the acting ROW'S own
   * `role="alert"` slot and nowhere else, because the page-level banner this view used to
   * render sat under the `<h1>` while the control that failed could be sections below it
   * — the onboarding submit is the furthest. Nothing scrolls and nothing steals focus: the
   * administrator just pressed the control, so the control is in the viewport by
   * construction and the message renders beside it; focus is already on the control and
   * `aria-describedby` ties the slot to it, so `role="alert"` announces it once and it
   * stays discoverable on re-focus. (`deferred-work.md:787` proposed "scroll/focus it on
   * failure" — a deliberate deviation: that was written from the symptom rather than the
   * cause, and `scrollIntoView` is not implemented in jsdom, so it would be untestable
   * machinery for a case that cannot arise. If a future layout ever does put an acting
   * control off-screen, the fix is a guarded `el.scrollIntoView?.(…)`.)
   *
   * `onError` (Story 8.5) lets ONE caller redirect the failure without forking the
   * guard: the confirmed merge runs behind a `ConfirmModal`, and the device row's own
   * alert slot sits behind the scrim where the administrator cannot see it, so that path
   * shows the message inside the still-open dialog instead. Every other action keeps its
   * row slot. The in-flight guard, the notice, the link and the refresh are shared by
   * all of them — which is the point: a confirmed merge must not be the one action that
   * can double-submit.
   *
   * A redirected failure must still land SOMEWHERE: that caller checks whether its dialog is
   * actually on screen and falls back to the loser device's OWN row slot when it is not,
   * because `ConfirmModal` dismisses on Esc and on a scrim click regardless of `busy`.
   * Redirecting unconditionally is how a destructive action's refusal reached no surface at
   * all; the per-row move only changed where the fallback lands, not that it is needed.
   */
  async function act(
    key: string,
    fn: () => Promise<string | { message: string; link?: string }>,
    onError?: (message: string) => void,
  ): Promise<void> {
    if (submitting) return;
    setActionError(null);
    setNotice(null);
    setLink(null);
    setSubmitting(true);
    let ok = false;
    try {
      const result = await fn();
      if (typeof result === 'string') {
        setNotice(result);
      } else {
        setNotice(result.message);
        setLink(result.link ?? null);
      }
      ok = true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Action failed';
      if (onError) onError(message);
      else setActionError({ key, message });
    } finally {
      setSubmitting(false);
    }
    // Refresh OUTSIDE the action try/catch so a refresh failure can't turn a successful
    // action into an error or wipe the success confirmation.
    if (ok) await load();
  }

  /**
   * Open the curation form on a proposal, clearing every message the last action left.
   *
   * It clears the notice and the link, following the CONFIRMED-MERGE precedent rather than
   * `refuse()`'s — and the difference is the one Story 8.10 review round 3 litigated. AA's
   * ruling there was that "exactly one message exists at a time" is an invariant of STARTING
   * an action, not of sending a request: `setPendingMerge` was made to clear on exactly that
   * ground, while `refuse()` was made to clear NOTHING because a short-circuit that sends
   * nothing supersedes nothing (it was destroying a non-re-mintable activation link). Opening
   * this form is starting an action, so it clears — the same shape as the merge dialog, which
   * can likewise be cancelled without sending anything. Inventing a third policy for a third
   * dialog is how the round-3 defect would come back; if this rule is ever revisited, the two
   * dialogs must move together.
   */
  function openEditor(next: Editing): void {
    setEditError(null);
    setEditAdvisory(null);
    setNotice(null);
    setLink(null);
    setActionError(null);
    setDiscardWarned(false);
    setEditing(next);
  }

  /**
   * Save the curation form (Story 8.12).
   *
   * Deliberately NOT routed through `act()`: `act()` owns the section's row-slot failure
   * surface and its notice/link clears, and this failure belongs inside the still-open dialog
   * (the row behind the scrim is not visible). It shares the reload, which is also what
   * refreshes the candidate lists — so a rename's NEW collisions appear immediately after the
   * save. That they appear after rather than before is the honest cost of permitting a rename
   * at all (AD-25 clause 6: recoverable is disclosed, irreversible is refused), and it is
   * logged rather than papered over with a per-keystroke probe.
   */
  async function onSaveCuration(): Promise<void> {
    if (!editing) return;
    const body: Record<string, string | null> = {};
    for (const f of editing.fields) {
      const draft = editing.draft[f.field] ?? '';
      const trimmed = draft.trim();
      const { to, changed } = intendedChange(f, draft);
      // The blank guards run only on a field the administrator ACTUALLY CHANGED (review
      // 2026-08-03). They used to run over every field before `intendedChange`, so a
      // proposal whose STORED value was whitespace-only — the legacy/import row the server's
      // own "a whitespace-only stored value is still CLEARABLE" case exists for — seeded a
      // whitespace-only draft and refused the whole save, with an advisory naming a field the
      // administrator never opened. On the required side it was worse: a stored model of
      // '   ' made every OTHER field unsavable. A field nobody touched cannot be a typo.
      if (!changed) continue;
      // A required field may be neither cleared nor blanked. Said here as an ADVISORY rather
      // than sent for the server's 400: the administrator has typed nothing wrong yet, they
      // have removed something, and the form can say so without a round trip.
      if (f.required && trimmed === '') {
        setEditError(null);
        setEditAdvisory(`${f.label} cannot be empty — it is what this record is.`);
        return;
      }
      // A field holding only whitespace is a TYPO, not a clear. Sending `null` here would
      // route around the server's blank→400 rule (AR-19) off a stray space; clearing stays
      // possible and stays explicit — empty the field completely.
      if (!f.required && draft !== '' && trimmed === '') {
        setEditError(null);
        setEditAdvisory(
          `A blank is not a clear. To remove ${f.label}, empty the field completely; otherwise type the value to record.`,
        );
        return;
      }
      body[f.field] = to;
    }
    // Nothing changed → no request at all: no phantom audit row, no spurious 400. An advisory,
    // not an error — the administrator did nothing wrong.
    if (Object.keys(body).length === 0) {
      setEditError(null);
      setEditAdvisory('No changes to save.');
      return;
    }
    setEditBusy(true);
    setEditError(null);
    setEditAdvisory(null);
    try {
      if (editing.kind === 'vendor') await curateProposedVendor(editing.id, body);
      else await curateProposedDevice(editing.id, body);
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : "Couldn't save. Try again.");
      setEditBusy(false);
      return;
    }
    setEditBusy(false);
    // Named with the same `f.label` the dialog just showed, NOT the wire keys (review
    // 2026-08-03). `body` is keyed by contract field names, so this used to announce
    // "Updated primaryProduct, complianceContact on Acme Radar Co" two lines from the inputs
    // labelled "Primary product" and "Compliance contact". The regression test passed only
    // because `name` is the one key that happens to read as English.
    const changedLabels = editing.fields
      .filter((f) => Object.prototype.hasOwnProperty.call(body, f.field))
      .map((f) => f.label.toLowerCase());
    // The subject as it read BEFORE the edit, which is how the administrator identified it.
    setNotice(`Updated ${changedLabels.join(', ')} on ${editing.subject}. Verify it to promote it.`);
    setEditing(null);
    setEditAdvisory(null);
    setDiscardWarned(false);
    // The reload is what refreshes the shortlists — see this function's docblock.
    await load();
  }

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Requests</h1>
      <p className="muted" style={{ margin: 0 }}>
        Curate Manager-proposed Vendors and Devices into the shared backbone (verify or
        merge a duplicate), and onboard Vendors that were sent an information request but
        have no portal account yet.
      </p>

      {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

      {state.kind === 'error' && (
        <div className="stack" style={{ gap: 8 }} role="alert">
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Story 8.10: there is deliberately NO page-level error banner here. A failure
          renders in the acting row's own slot — see `act()`. The success notice below
          stays page-level: it is one shared `role="status"`, it is not tied to a control
          the administrator is about to press again, and it carries the activation link. */}
      <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
        {notice && (
          <p className="muted" style={{ margin: 0, fontSize: 13, wordBreak: 'break-all' }}>
            {notice}
            {link && (
              <>
                {' '}
                Activation link (dev — no email sent): <span data-numeric>{link}</span>
              </>
            )}
          </p>
        )}
      </div>

      {state.kind === 'ready' && (
        <>
          <ProposedVendorsSection
            items={state.data.inbox.proposedVendors}
            targets={state.data.backboneVendors}
            selected={mergeTargets}
            onSelect={(id, target) => setMergeTargets((m) => ({ ...m, [id]: target }))}
            submitting={submitting}
            errorFor={errorFor}
            verifyRef={(id, el) => {
              vendorVerifyRefs.current[id] = el;
            }}
            onVerify={(id) =>
              void act(`vendor-verify:${id}`, async () => {
                await verifyProposedVendor(id);
                return 'Vendor verified into the directory.';
              })
            }
            onMerge={(id, target) =>
              void act(`vendor-merge:${id}`, async () => {
                await mergeProposedVendor(id, target);
                return 'Proposed Vendor merged into the selected record.';
              })
            }
            onEdit={(item) =>
              openEditor({
                kind: 'vendor',
                id: item.vendor.id,
                subject: item.vendor.name,
                fields: [
                  { field: 'name', label: 'Name', required: true, stored: item.vendor.name },
                  {
                    field: 'country',
                    label: 'Country',
                    required: false,
                    stored: item.vendor.country ?? null,
                  },
                  {
                    field: 'category',
                    label: 'Category',
                    required: false,
                    stored: item.vendor.category ?? null,
                  },
                  {
                    field: 'primaryProduct',
                    label: 'Primary product',
                    required: false,
                    stored: item.vendor.primaryProduct ?? null,
                  },
                  {
                    field: 'complianceContact',
                    label: 'Compliance contact',
                    required: false,
                    stored: item.vendor.complianceContact ?? null,
                  },
                ],
                draft: {
                  name: item.vendor.name,
                  country: item.vendor.country ?? '',
                  category: item.vendor.category ?? '',
                  primaryProduct: item.vendor.primaryProduct ?? '',
                  complianceContact: item.vendor.complianceContact ?? '',
                },
              })
            }
          />

          <ProposedDevicesSection
            items={state.data.inbox.proposedDevices}
            targets={state.data.backboneDevices}
            blockingVendors={state.data.inbox.proposedVendors}
            selected={mergeTargets}
            onSelect={(id, target) => setMergeTargets((m) => ({ ...m, [id]: target }))}
            submitting={submitting}
            errorFor={errorFor}
            onBlocked={(id, message) => refuse(`device-verify:${id}`, message)}
            onGoToVendor={(vendorId) => vendorVerifyRefs.current[vendorId]?.focus()}
            onVerify={(id) =>
              void act(`device-verify:${id}`, async () => {
                await verifyProposedDevice(id);
                return 'Device verified into the directory.';
              })
            }
            onMerge={(id, target) => {
              // Story 8.5 (AD-25): when the chosen target's revision differs from the
              // proposal's, confirm BEFORE any request is sent — a merge deletes the
              // loser's row and its revision with it, and the admin is the only one who
              // can say that is intended. Everything else posts straight through, with
              // no acknowledgement, exactly as before.
              const loser = state.kind === 'ready'
                ? state.data.inbox.proposedDevices.find((d) => d.device.id === id)
                : undefined;
              const winner = state.kind === 'ready'
                ? state.data.backboneDevices.find((d) => d.device.id === target)
                : undefined;
              if (loser && winner && mergeDestroysRevision(loser.device, winner.device)) {
                setMergeError(null);
                // This branch opens the dialog INSTEAD of calling `act()`, so it must run
                // `act()`'s entry clears itself (review round 3): it was the one action
                // start that did not, leaving a previous row's refusal on screen and a
                // stale success notice at the top of the page while a destructive
                // confirmation was open. "Exactly one message exists at a time" is an
                // invariant of starting an action, not of sending a request.
                setActionError(null);
                setNotice(null);
                setLink(null);
                setPendingMerge({ loser, winner });
                return;
              }
              void act(`device-merge:${id}`, async () => {
                await mergeProposedDevice(id, target);
                return 'Proposed Device merged into the selected record.';
              });
            }}
            onEdit={(item) =>
              openEditor({
                kind: 'device',
                id: item.device.id,
                // The LABEL, not the bare model: two proposals of one model can sit in this
                // queue — the state AD-25 exists to disambiguate.
                subject: deviceLabel(item.device),
                fields: [
                  { field: 'model', label: 'Model', required: true, stored: item.device.model },
                  {
                    field: 'category',
                    label: 'Category',
                    required: false,
                    stored: item.device.category ?? null,
                  },
                  {
                    field: 'version',
                    label: 'Revision',
                    required: false,
                    stored: item.device.version ?? null,
                  },
                ],
                draft: {
                  model: item.device.model,
                  category: item.device.category ?? '',
                  version: item.device.version ?? '',
                },
              })
            }
          />

          <OnboardingSection
            items={state.data.inbox.onboardingFlags}
            emails={emails}
            onEmail={(id, email) => setEmails((e) => ({ ...e, [id]: email }))}
            submitting={submitting}
            errorFor={errorFor}
            onResolve={(id, email) =>
              void act(`onboard:${id}`, async () => {
                const result = await resolveOnboarding(id, email);
                if (!result.invited) {
                  return 'Vendor already has an account — request resolved, no invite needed.';
                }
                // Dedup path: a live invitation already existed for this Vendor, so none was
                // minted this call (the flag is still resolved). Don't claim a fresh send or
                // surface a link that wasn't issued now.
                if (!result.invitation) {
                  return 'A live onboarding invitation already exists — request resolved.';
                }
                return {
                  message: 'Onboarding invitation sent.',
                  link: result.invitation.activationLink,
                };
              })
            }
          />
        </>
      )}

      {editing && (
        <ConfirmModal
          title="Edit before verifying"
          confirmLabel="Save"
          busy={editBusy}
          error={editError}
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                Correct <strong>{editing.subject}</strong> before it is promoted. Verifying
                writes this row into the shared backbone <em>as it stands</em> under the same
                id, so this is the last point at which a correction costs nothing — afterwards
                every Company reads it. Leave an optional field empty to clear it;{' '}
                {editing.kind === 'vendor' ? 'the name' : 'the model'} cannot be empty. This
                action is logged, including what each value was changed from.
              </p>
              {editing.fields.map((f) => (
                <label key={f.field} className="stack" style={{ gap: 4 }}>
                  <span className="field-label">
                    {f.label}
                    {f.required ? '' : ' (optional)'}
                  </span>
                  {/*
                    NO `aria-label` (review 2026-08-03). This `<label>` already wraps the input,
                    so the visible text above IS the accessible name — including the
                    "(optional)" suffix. An `aria-label={f.label}` WINS the accessible-name
                    computation and dropped that suffix, so the visible label "Category
                    (optional)" was not contained in the accessible name "Category": WCAG 2.5.3
                    Label in Name, which breaks voice control ("click Category optional" matches
                    nothing) and never tells a screen-reader user the field is clearable. The
                    tests queried it BOTH ways, which is why neither noticed the two disagreed.
                  */}
                  <input
                    value={editing.draft[f.field] ?? ''}
                    onChange={(e) => {
                      setEditing({
                        ...editing,
                        draft: { ...editing.draft, [f.field]: e.target.value },
                      });
                      // Typing after a discard warning re-arms it: the warning is about THIS
                      // draft, and the draft just changed.
                      setDiscardWarned(false);
                      setEditError(null);
                      setEditAdvisory(null);
                    }}
                    style={fieldStyle}
                  />
                </label>
              ))}
              {/* The dialog's POLITE advisory slot — see `editAdvisory`. `ConfirmModal`'s own
                  assertive region stays reserved for a server failure. */}
              <div role="status" aria-live="polite">
                {editAdvisory && (
                  <span className="status-pill" data-tone="warn">
                    <span className="status-pill__dot" aria-hidden="true" />
                    {editAdvisory}
                  </span>
                )}
              </div>
            </div>
          }
          onCancel={() => {
            // Esc, a scrim click and Cancel all arrive here and `ConfirmModal` cannot tell
            // them apart — which is exactly why the warning covers all three rather than only
            // the two incidental ones. Both sides trimmed, as `onSaveCuration` compares them,
            // so opening a padded row merely to LOOK at it does not report unsaved changes.
            const dirty = editing.fields.some(
              (f) => intendedChange(f, editing.draft[f.field] ?? '').changed,
            );
            if (dirty && !discardWarned) {
              setDiscardWarned(true);
              // A deliberate-confirmation prompt is not an error: polite region, `warn` tone.
              // The assertive one is left alone so a server refusal already on screen is
              // PRESERVED — dismissing must not destroy the reason the save failed, which is
              // the one thing that explains why there are unsaved changes at all.
              setEditAdvisory(
                'You have unsaved changes to this proposal. Dismiss again to discard them, or press Save to record them.',
              );
              return;
            }
            setEditing(null);
            setEditError(null);
            setEditAdvisory(null);
            setDiscardWarned(false);
          }}
          onConfirm={() => void onSaveCuration()}
        />
      )}

      {pendingMerge && (
        <ConfirmModal
          title="Merge will delete a revision"
          confirmLabel="Merge and discard the revision"
          busy={submitting}
          error={mergeError}
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                The proposal <strong>{deviceLabel(pendingMerge.loser.device)}</strong> carries
                revision <strong>{deviceRevision(pendingMerge.loser.device)}</strong>. The target{' '}
                <strong>{deviceLabel(pendingMerge.winner.device)}</strong>{' '}
                {deviceRevision(pendingMerge.winner.device) === ''
                  ? 'has no revision recorded'
                  : `is revision ${deviceRevision(pendingMerge.winner.device)}`}
                .
              </p>
              <p style={{ margin: 0 }}>
                Merging deletes the proposal and re-points its installs, requests and
                declarations to the target — so revision{' '}
                <strong>{deviceRevision(pendingMerge.loser.device)}</strong> is discarded and
                cannot be recovered. If these are genuinely different hardware, Verify the
                proposal instead: that promotes it in place and keeps its revision. This action
                is logged, including what was discarded.
              </p>
            </div>
          }
          onCancel={() => {
            mergeDialogOpen.current = false;
            setPendingMerge(null);
            setMergeError(null);
          }}
          onConfirm={() => {
            const { loser, winner } = pendingMerge;
            mergeDialogOpen.current = true;
            setMergeError(null);
            // Story 8.5: the confirmed merge runs through the SECTION'S shared `act()`
            // guard, exactly like Verify and every other curation action — a confirmed
            // merge is still one destructive POST and must not be the one path where a
            // double-click can fire two. `act` owns the in-flight flag (which is also
            // this dialog's `busy`), the success notice and the reload; only the FAILURE
            // is redirected, into the open dialog, because the row's alert slot is
            // behind the scrim (ConfirmModal contract).
            void act(
              `device-merge:${loser.device.id}`,
              async () => {
                // The acknowledgement travels ONLY from this confirmed path; the server's
                // 409 remains the guarantee for every caller that skips the modal.
                await mergeProposedDevice(loser.device.id, winner.device.id, true);
                mergeDialogOpen.current = false;
                setPendingMerge(null);
                return 'Proposed Device merged into the selected record; its revision was discarded.';
              },
              // The failure goes into the dialog while the dialog is there, and falls back to
              // the LOSER DEVICE'S OWN ROW SLOT once it is not (Story 8.10 — it used to fall
              // back to the page-level banner, which no longer exists; the row is where the
              // untouched proposal still sits, so it is where the refusal belongs). Esc or a
              // scrim click during the POST would otherwise send a destructive action's
              // refusal nowhere at all.
              (message) =>
                mergeDialogOpen.current
                  ? setMergeError(message)
                  : setActionError({ key: `device-merge:${loser.device.id}`, message }),
            );
          }}
        />
      )}
    </>
  );
}

function ProposedVendorsSection({
  items,
  targets,
  selected,
  onSelect,
  submitting,
  errorFor,
  verifyRef,
  onVerify,
  onMerge,
  onEdit,
}: {
  items: ProposedVendorItem[];
  targets: VendorDirectoryEntry[];
  selected: Record<string, string>;
  onSelect: (id: string, target: string) => void;
  submitting: boolean;
  /** This row's action failure, if the current one belongs to it (Story 8.10). */
  errorFor: (rowKey: string) => string | null;
  /** Registers each row's Verify button so a blocked Device row can send focus to it. */
  verifyRef: (id: string, el: HTMLButtonElement | null) => void;
  onVerify: (id: string) => void;
  onMerge: (id: string, target: string) => void;
  /** Opens the Story 8.12 curation form on this proposal. */
  onEdit: (item: ProposedVendorItem) => void;
}): React.JSX.Element {
  return (
    <section className="card stack" aria-labelledby="proposed-vendors-heading">
      <h2 id="proposed-vendors-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
        Proposed vendors
      </h2>
      {items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>No proposed vendors awaiting curation.</p>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 12 }}>
          {items.map((item) => {
            const slotId = `inbox-error-${item.vendor.id}`;
            return (
            <li
              key={item.vendor.id}
              className="stack"
              style={{ gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}
            >
              <div>
                <span style={{ fontWeight: 700 }}>{item.vendor.name}</span>{' '}
                <span className="muted" style={{ fontSize: 13 }}>
                  proposed by {item.proposedByCompanyName}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn-accent"
                  data-surface="accent"
                  disabled={submitting}
                  aria-describedby={slotId}
                  ref={(el) => {
                    verifyRef(item.vendor.id, el);
                  }}
                  onClick={() => onVerify(item.vendor.id)}
                >
                  Verify
                </button>
                <label className="stack" style={{ gap: 4 }}>
                  <span className="muted" style={{ fontSize: 12 }}>Merge into…</span>
                  <select
                    aria-label={`Merge ${item.vendor.name} into`}
                    // See the device section: the target picker is an acting control and
                    // the one a failed merge returns the administrator to (review round 3).
                    aria-describedby={slotId}
                    value={selected[item.vendor.id] ?? ''}
                    onChange={(e) => onSelect(item.vendor.id, e.target.value)}
                    style={fieldStyle}
                  >
                    <option value="">Select a vendor…</option>
                    {targets.map((t) => (
                      <option key={t.vendor.id} value={t.vendor.id}>
                        {t.vendor.name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="icon-button"
                  disabled={submitting || !selected[item.vendor.id]}
                  aria-describedby={slotId}
                  onClick={() => onMerge(item.vendor.id, selected[item.vendor.id] ?? '')}
                >
                  Merge
                </button>
                {/* Story 8.12: the third disposition. Promotion mutates this row in place, so
                    once it is verified whatever the Manager typed IS the shared-read record —
                    correcting it beforehand is the only moment at which it costs nothing. */}
                <button
                  type="button"
                  className="icon-button"
                  disabled={submitting}
                  aria-describedby={slotId}
                  aria-label={`Edit ${item.vendor.name} before verifying`}
                  onClick={() => onEdit(item)}
                >
                  Edit before verifying
                </button>
              </div>
              <DuplicateCandidates
                kind="vendor"
                rowId={item.vendor.id}
                subject={item.vendor.name}
                shortlist={item.duplicateCandidates}
                submitting={submitting}
                describedBy={slotId}
                // Sets the row's merge target AND runs the EXISTING merge, so nothing about
                // the merge path forks: the target is passed by value (no state race), and a
                // failure lands in this row's Story 8.10 slot.
                onMergeInto={(candidateId) => {
                  onSelect(item.vendor.id, candidateId);
                  onMerge(item.vendor.id, candidateId);
                }}
              />
              <RowErrorSlot id={slotId} message={errorFor(item.vendor.id)} />
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ProposedDevicesSection({
  items,
  targets,
  blockingVendors,
  selected,
  onSelect,
  submitting,
  errorFor,
  onBlocked,
  onGoToVendor,
  onVerify,
  onMerge,
  onEdit,
}: {
  items: ProposedDeviceItem[];
  targets: DeviceCatalogEntry[];
  /**
   * The inbox's proposed Vendors — the source of the blocking Vendor's NAME (Story 8.10).
   * `vendorProposed` states THAT a Device is blocked; only this list says by whom, and
   * both come out of the one `listInbox` transaction so they agree at read time. No
   * contract change was needed: `device.vendorId` and `proposedVendors[].vendor.id` are
   * both already on the wire.
   */
  blockingVendors: ProposedVendorItem[];
  selected: Record<string, string>;
  onSelect: (id: string, target: string) => void;
  submitting: boolean;
  errorFor: (rowKey: string) => string | null;
  /** A Verify the client refuses on the administrator's behalf — no request is sent. */
  onBlocked: (id: string, message: string) => void;
  /** Moves focus to the blocking Vendor's Verify button, which is on this same page. */
  onGoToVendor: (vendorId: string) => void;
  onVerify: (id: string) => void;
  onMerge: (id: string, target: string) => void;
  /** Opens the Story 8.12 curation form on this proposal. */
  onEdit: (item: ProposedDeviceItem) => void;
}): React.JSX.Element {
  /**
   * Story 8.10: blocked rows sort LAST, so "verify the Vendors first" is advice the
   * ordering agrees with rather than contradicts. `Array.prototype.sort` is stable, so
   * within each group the server's `created_at ASC, id ASC` order is preserved — the
   * server's ordering is not touched.
   */
  const ordered = [...items].sort((a, b) => Number(a.vendorProposed) - Number(b.vendorProposed));
  return (
    <section className="card stack" aria-labelledby="proposed-devices-heading">
      <h2 id="proposed-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
        Proposed devices
      </h2>
      {ordered.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>No proposed devices awaiting curation.</p>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 12 }}>
          {ordered.map((item) => {
            const slotId = `inbox-error-${item.device.id}`;
            const hintId = `inbox-blocked-${item.device.id}`;
            // Degrades to the unnamed sentence when no matching proposal is on the page —
            // it cannot normally happen (one transaction produces both lists), but an
            // empty name rendered next to a destructive queue is not an acceptable guess.
            const blocker = item.vendorProposed
              ? blockingVendors.find((v) => v.vendor.id === item.device.vendorId)
              : undefined;
            const reason = item.vendorProposed ? blockedReason(blocker?.vendor.name ?? null) : null;
            const slotMessage = errorFor(item.device.id);
            // The blocked reason has ONE rendered source at a time (review round 3).
            // Before the click it is the static hint; once the short-circuit has echoed the
            // same sentence into the row's alert slot, the hint's copy is suppressed and
            // only the slot describes the control. Rendering both put the identical
            // sentence on screen twice and named both from `aria-describedby`, so a screen
            // reader announced it on the alert and read it twice more on re-focus — the
            // exact "two live regions carrying one sentence" mode `RowErrorSlot`'s docblock
            // cites AD-22 rule 7 against, reintroduced inside the row.
            const reasonEchoed = reason !== null && slotMessage === reason;
            return (
            <li
              key={item.device.id}
              className="stack"
              style={{ gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}
            >
              <div>
                <span style={{ fontWeight: 700 }}>{item.device.model}</span>{' '}
                {/* Story 7.1: the proposed hardware/firmware revision is part of
                    WHAT is being verified — the admin must see it before deciding. */}
                {item.device.version ? (
                  <>
                    <span data-numeric>{item.device.version}</span>{' '}
                  </>
                ) : null}
                <span className="muted" style={{ fontSize: 13 }}>
                  by {item.vendorName} · proposed by {item.proposedByCompanyName}
                </span>
              </div>
              {reason !== null && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  {!reasonEchoed && (
                    <span id={hintId} className="muted" style={{ fontSize: 12 }}>
                      {reason}
                    </span>
                  )}
                  {blocker && (
                    <button
                      type="button"
                      className="icon-button"
                      // Its target is `disabled={submitting}`, and `.focus()` on a disabled
                      // element is a no-op — so while any action was in flight this button
                      // moved nothing, announced nothing and reported nothing (review round
                      // 3). Share the in-flight guard rather than fail silently.
                      disabled={submitting}
                      onClick={() => onGoToVendor(item.device.vendorId)}
                    >
                      Go to {blocker.vendor.name}
                    </button>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                {/* Story 8.10: `aria-disabled`, NOT `disabled`, for the blocked state. A
                    natively disabled button is out of the tab order, so its reason — the
                    hint above, tied on by `aria-describedby` — could never be reached by a
                    keyboard user at all; "discovering it by failing" is not satisfied by a
                    control that cannot be reached to fail. `disabled={submitting}` keeps
                    the in-flight guard, which is a different concern. The blocked click is
                    short-circuited here rather than sent: the server would 409, and this
                    row already knows it. (A stale page is the residual case and it still
                    works — `vendorProposed` is false, the request goes, and the server's
                    409 lands in this row's slot.) */}
                <button
                  type="button"
                  className="btn-accent"
                  data-surface="accent"
                  disabled={submitting}
                  aria-disabled={item.vendorProposed}
                  aria-describedby={reason !== null && !reasonEchoed ? `${hintId} ${slotId}` : slotId}
                  onClick={() => {
                    if (reason !== null) {
                      onBlocked(item.device.id, reason);
                      return;
                    }
                    onVerify(item.device.id);
                  }}
                >
                  Verify
                </button>
                <label className="stack" style={{ gap: 4 }}>
                  <span className="muted" style={{ fontSize: 12 }}>Merge into…</span>
                  {/* The control's own NAME is composed through the owner too, not just its
                      options: two proposals of one model can sit in this list — the state
                      AD-25 exists to disambiguate — and a bare-model `aria-label` gave them
                      two identically named comboboxes on the one action that DELETES a row.
                      Same reasoning as `CuratedDevicesView`'s Edit control. */}
                  <select
                    aria-label={`Merge ${deviceLabel(item.device)} into`}
                    // Task 1's "every acting control in that row" includes the target
                    // picker and the onboarding email input, not only the buttons — the
                    // Code Map names all three (review round 3). A merge that 409s leaves
                    // the selection in place, so the control the administrator returns to
                    // is this one, and it has to be able to say why.
                    aria-describedby={slotId}
                    value={selected[item.device.id] ?? ''}
                    onChange={(e) => onSelect(item.device.id, e.target.value)}
                    style={fieldStyle}
                  >
                    <option value="">Select a device…</option>
                    {/* Story 7.1/8.5: since `version` carries no uniqueness constraint,
                        two catalog rows can differ ONLY by revision — and merging
                        deletes the loser, so an ambiguous option here is an
                        unrecoverable pick. The label comes from the ONE web owner
                        (AD-25); the copy of `SiteDetail`'s concatenation that used to
                        live here is gone. */}
                    {targets.map((t) => (
                      <option key={t.device.id} value={t.device.id}>
                        {deviceLabel(t.device)} ({t.vendor.name})
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="icon-button"
                  disabled={submitting || !selected[item.device.id]}
                  aria-describedby={slotId}
                  onClick={() => onMerge(item.device.id, selected[item.device.id] ?? '')}
                >
                  Merge
                </button>
                {/* Story 8.12. Available even on a BLOCKED row: curating a device is not a
                    promotion, so the owning Vendor's own state does not gate it — and a
                    blocked row is precisely the one an administrator is looking at while they
                    wait to be able to verify it. */}
                <button
                  type="button"
                  className="icon-button"
                  disabled={submitting}
                  aria-describedby={slotId}
                  // The LABEL, not the bare model: two proposals of one model can sit in this
                  // queue, and a bare-model name would give them two identical Edit controls.
                  aria-label={`Edit ${deviceLabel(item.device)} before verifying`}
                  onClick={() => onEdit(item)}
                >
                  Edit before verifying
                </button>
              </div>
              <DuplicateCandidates
                kind="device"
                rowId={item.device.id}
                subject={deviceLabel(item.device)}
                shortlist={item.duplicateCandidates}
                submitting={submitting}
                describedBy={slotId}
                // Sets the target AND runs the EXISTING merge — including Story 8.5's confirm
                // modal, because that decision is made inside `onMerge` from the target id.
                onMergeInto={(candidateId) => {
                  onSelect(item.device.id, candidateId);
                  onMerge(item.device.id, candidateId);
                }}
              />
              <RowErrorSlot id={slotId} message={errorFor(item.device.id)} />
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function OnboardingSection({
  items,
  emails,
  onEmail,
  submitting,
  errorFor,
  onResolve,
}: {
  items: OnboardingRequestItem[];
  emails: Record<string, string>;
  onEmail: (id: string, email: string) => void;
  submitting: boolean;
  /**
   * This section is the furthest from the top of the page, so it is the one the removed
   * page-level banner served worst: its submit sits three sections below where that
   * banner rendered (Story 8.10).
   */
  errorFor: (rowKey: string) => string | null;
  onResolve: (id: string, email: string) => void;
}): React.JSX.Element {
  return (
    <section className="card stack" aria-labelledby="onboarding-heading">
      <h2 id="onboarding-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
        Onboarding requests
      </h2>
      {items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>No onboarding requests awaiting action.</p>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 12 }}>
          {items.map((item) => {
            const slotId = `inbox-error-${item.id}`;
            return (
            <li
              key={item.id}
              className="stack"
              style={{ gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}
            >
              <div>
                <span style={{ fontWeight: 700 }}>{item.vendorName}</span>{' '}
                <span className="muted" style={{ fontSize: 13 }}>
                  requested by {item.requestedByCompanyName}
                </span>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onResolve(item.id, emails[item.id] ?? '');
                }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}
              >
                <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
                  <span className="field-label">Vendor email</span>
                  <input
                    aria-label={`Onboard ${item.vendorName} email`}
                    // The 400 this form takes (a bad or already-onboarded email) is ABOUT
                    // this input, and it is the control the administrator is returning to
                    // (review round 3).
                    aria-describedby={slotId}
                    type="email"
                    required
                    value={emails[item.id] ?? ''}
                    onChange={(e) => onEmail(item.id, e.target.value)}
                    style={fieldStyle}
                  />
                </label>
                <button
                  type="submit"
                  className="btn-accent"
                  data-surface="accent"
                  disabled={submitting}
                  aria-describedby={slotId}
                >
                  Onboard vendor
                </button>
              </form>
              <RowErrorSlot id={slotId} message={errorFor(item.id)} />
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
